// PKCE helpers (RFC 7636). Mirrors the backend `crypto/sha256` +
// `base64.RawURLEncoding` path so the verifier the SPA generates here
// hashes to the same challenge the AS receives at /authorize and the
// same value /token verifies on exchange.

const VERIFIER_BYTES = 32;

function base64url(bytes: Uint8Array): string {
  // String.fromCharCode requires explicit number args; iterate via
  // .reduce so TS knows each element is non-undefined (Uint8Array
  // values are always numbers; the `?` typing is an artifact of the
  // ES2022 array indexing rule).
  const s = bytes.reduce((acc, b) => acc + String.fromCharCode(b), "");
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

// generateCodeVerifier produces a high-entropy URL-safe verifier
// (RFC 7636 §4.1: 43-128 chars). 32 bytes -> 43 base64url chars.
export function generateCodeVerifier(): string {
  const buf = new Uint8Array(VERIFIER_BYTES);
  crypto.getRandomValues(buf);
  return base64url(buf);
}

// codeChallengeFromVerifier returns base64url(SHA-256(verifier)).
// Matches the Go side `sha256.Sum256(...); base64.RawURLEncoding`.
export async function codeChallengeFromVerifier(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64url(new Uint8Array(digest));
}

// randomState + randomNonce share the verifier RNG path; both must be
// unpredictable and bound to the originating SPA tab.
export function randomState(): string {
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  return base64url(buf);
}

export function randomNonce(): string {
  return randomState();
}
